export type LayoutReview = {
  hard: string[]
  notes: string[]
}

export function reviewToResult(
  review: LayoutReview,
  okText: string,
): { status: 'ok' | 'fail'; message: string } {
  const note = review.notes.length ? ` Lưu ý: ${review.notes.join('; ')}.` : ''
  if (review.hard.length) {
    return { status: 'fail', message: `${review.hard.join('; ')}.${note}` }
  }
  return { status: 'ok', message: `${okText}.${note}` }
}

export function controlLabel(el: Element): string {
  const aria = el.getAttribute('aria-label')
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim()
  return (aria || text || el.tagName).slice(0, 80)
}

function fieldShell(input: HTMLElement): HTMLElement {
  return (input.closest('.MuiFormControl-root') as HTMLElement | null) ?? input
}

function overlapArea(a: DOMRect, b: DOMRect): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left)
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
  if (w <= 1 || h <= 1) return 0
  return w * h
}

function boxInside(inner: DOMRect, outer: DOMRect, pad = 4): boolean {
  return (
    inner.left >= outer.left - pad &&
    inner.right <= outer.right + pad &&
    inner.top >= outer.top - pad &&
    inner.bottom <= outer.bottom + pad
  )
}

export function auditStackedFields(fields: HTMLElement[], region: HTMLElement, review: LayoutReview): void {
  const regionBox = region.getBoundingClientRect()
  const boxes = fields.map((field) => fieldShell(field).getBoundingClientRect())
  for (let i = 0; i < boxes.length; i += 1) {
    const box = boxes[i]
    if (box.width < regionBox.width * 0.55) {
      review.hard.push(`Ô "${controlLabel(fields[i])}" hẹp hơn một nửa khung form`)
    }
    if (!boxInside(box, regionBox, 8)) {
      review.hard.push(`Ô "${controlLabel(fields[i])}" tràn ra ngoài khung`)
    }
    if (i === 0) continue
    const prev = boxes[i - 1]
    if (box.top < prev.bottom - 2) {
      review.hard.push(
        `Ô "${controlLabel(fields[i])}" đè lên ô "${controlLabel(fields[i - 1])}"`,
      )
    } else if (overlapArea(prev, box) > 0) {
      review.hard.push(`Hai ô liên tiếp bị chồng`)
    } else if (box.top - prev.bottom > 48) {
      review.notes.push(`Khoảng cách giữa hai ô lớn (${Math.round(box.top - prev.bottom)}px)`)
    }
  }
}

export type FormChromeReport = {
  /** Vùng overflow thật sự phải kéo (nội dung cao hơn khung nhìn). */
  scrolls: string[]
  /** Hủy/Lưu nằm ở footer dialog, ngoài thân form. */
  footerOutsideBody: boolean
  fieldLabels: string[]
}

function shortNodeName(el: HTMLElement): string {
  if (el.classList.contains('MuiDialogContent-root')) return 'thân dialog'
  if (el.classList.contains('MuiDialog-paper')) return 'khung dialog'
  if (el.classList.contains('kiot-form-field')) return 'ô nhập'
  const cls = Array.from(el.classList).find((name) => name.startsWith('Mui')) ?? el.tagName.toLowerCase()
  return cls
}

/** Form ngắn (vài ô) theo base thì không được bắt người dùng cuộn. */
export function inspectFormChrome(dialog: HTMLElement): FormChromeReport {
  const scrolls: string[] = []
  const nodes: HTMLElement[] = [dialog, ...Array.from(dialog.querySelectorAll<HTMLElement>('*'))]
  for (const el of nodes) {
    if (el.closest('[data-ui-test-overlay="true"]')) continue
    const oy = getComputedStyle(el).overflowY
    if (oy !== 'auto' && oy !== 'scroll') continue
    const extra = el.scrollHeight - el.clientHeight
    if (el.clientHeight > 40 && extra > 4) {
      scrolls.push(
        `${shortNodeName(el)} cao ${el.clientHeight}px nhưng nội dung ${el.scrollHeight}px (phải cuộn ${extra}px)`,
      )
    }
  }
  const labels = Array.from(dialog.querySelectorAll('label'))
    .map((node) => (node.textContent ?? '').replace(/\s+/g, ' ').trim().replace(/\*$/, '').trim())
    .filter((text) => text && text !== 'Hủy' && text !== 'Lưu')
  return {
    scrolls,
    footerOutsideBody: Boolean(dialog.querySelector('.MuiDialogActions-root')),
    fieldLabels: Array.from(new Set(labels)),
  }
}

export function auditDialogFrame(dialog: HTMLElement, review: LayoutReview): void {
  const box = dialog.getBoundingClientRect()
  if (box.width < 280) review.hard.push('Hộp thoại quá hẹp')
  if (box.top < -12 || box.left < -12) review.hard.push('Hộp thoại bị cắt phía trên/trái màn hình')
  if (box.bottom > window.innerHeight + 12 || box.right > window.innerWidth + 12) {
    review.hard.push('Hộp thoại tràn khỏi khung nhìn')
  }
}

export function auditListChrome(screen: HTMLElement): LayoutReview {
  const review: LayoutReview = { hard: [], notes: [] }
  const table = screen.querySelector('table')
  if (!table) {
    review.hard.push('Không thấy bảng danh sách')
    return review
  }

  const headerText = (table.querySelector('thead')?.textContent ?? '').replace(/\s+/g, ' ')
  for (const label of ['Mã nhóm', 'Tên nhóm', 'Cấp', 'Trạng thái', 'Thao tác']) {
    if (!headerText.includes(label)) review.hard.push(`Thiếu cột "${label}"`)
  }

  const search = screen.querySelector('input[placeholder="Theo mã, tên nhóm"]')
  if (!search) review.hard.push('Không thấy ô tìm theo mã, tên nhóm')
  const filterBtn = screen.querySelector('button[aria-label="Lọc"]')
  if (!filterBtn) review.hard.push('Không thấy nút mở bộ lọc')
  if (!buttonIncludes(screen, 'Thêm nhóm hàng')) review.hard.push('Không thấy nút Thêm nhóm hàng')
  if (!buttonIncludes(screen, 'Dạng cây')) review.hard.push('Không thấy nút Dạng cây')

  const sidebarText = sidebarOf(screen)?.textContent ?? ''
  for (const label of ['Trạng thái', 'Sắp xếp', 'Đang hoạt động', 'Ngừng hoạt động', 'Tất cả']) {
    if (!sidebarText.includes(label)) review.notes.push(`Sidebar chưa thấy "${label}"`)
  }

  const scroller = table.parentElement
  if (scroller && scroller.scrollWidth > scroller.clientWidth + 8) {
    review.notes.push('Bảng rộng hơn khung, cần cuộn ngang để thấy cột cuối')
  }

  const toolbarButtons = Array.from(screen.querySelectorAll('button')).filter((btn) => {
    const text = controlLabel(btn)
    return text === 'Thêm nhóm hàng' || text === 'Dạng cây' || text === 'Lọc'
  })
  for (let i = 0; i < toolbarButtons.length; i += 1) {
    for (let j = i + 1; j < toolbarButtons.length; j += 1) {
      const a = toolbarButtons[i].getBoundingClientRect()
      const b = toolbarButtons[j].getBoundingClientRect()
      if (overlapArea(a, b) > 20) {
        review.hard.push(`Nút "${controlLabel(toolbarButtons[i])}" đè nút "${controlLabel(toolbarButtons[j])}"`)
      }
    }
  }

  return review
}

export function auditFilterPopover(paper: HTMLElement): LayoutReview {
  const review: LayoutReview = { hard: [], notes: [] }
  const inputs = Array.from(paper.querySelectorAll('input')) as HTMLInputElement[]
  if (inputs.length < 4) {
    review.hard.push(`Popover lọc có ${inputs.length} ô, cần 4 ô (mã, tên, mã cha, nhóm cha)`)
  }
  const text = paper.textContent ?? ''
  for (const label of ['Mã nhóm', 'Tên nhóm', 'Mã nhóm cha', 'Nhóm cha', 'Tìm kiếm', 'Đặt lại']) {
    if (!text.includes(label)) review.hard.push(`Popover thiếu "${label}"`)
  }
  auditStackedFields(inputs.slice(0, 4), paper, review)
  const box = paper.getBoundingClientRect()
  if (box.bottom > window.innerHeight - 4) review.notes.push('Popover lọc chạm đáy màn hình')
  if (box.width < 240) review.hard.push('Popover lọc quá hẹp')
  return review
}

export function auditNhomHangForm(dialog: HTMLElement, mode: 'create' | 'edit'): LayoutReview {
  const review: LayoutReview = { hard: [], notes: [] }
  auditDialogFrame(dialog, review)
  const ma = dialog.querySelector('input[name="maNhom"]') as HTMLInputElement | null
  const ten = dialog.querySelector('input[name="tenNhom"]') as HTMLInputElement | null
  const parent = inputByLabel(dialog, 'Nhóm cha')
  if (!ma) review.hard.push('Form thiếu ô Mã nhóm')
  if (!ten) review.hard.push('Form thiếu ô Tên nhóm')
  if (!parent) review.hard.push('Form thiếu ô chọn Nhóm cha')

  // Form nhóm hàng xếp dọc (Mã → Tên → Nhóm cha), không xếp ngang.
  const fields = [ma, ten, parent].filter((el): el is HTMLInputElement => Boolean(el))
  const body =
    (dialog.querySelector('.MuiDialogContent-root') as HTMLElement | null) ?? dialog
  auditStackedFields(fields, body, review)

  const actions = (dialog.querySelector('.MuiDialogActions-root') as HTMLElement | null) ?? dialog
  if (!buttonIncludes(actions, 'Hủy')) review.hard.push('Form thiếu nút Hủy')
  if (!buttonIncludes(actions, 'Lưu')) review.hard.push('Form thiếu nút Lưu')
  const save = findButton(actions, 'Lưu')
  const lastField = fields[fields.length - 1]
  if (save && lastField) {
    const saveBox = save.getBoundingClientRect()
    const fieldBox = fieldShell(lastField).getBoundingClientRect()
    if (saveBox.top < fieldBox.bottom - 4) review.hard.push('Nút Lưu đè lên ô nhập cuối')
  }

  if (ma && !ma.disabled) {
    review.hard.push(
      mode === 'edit'
        ? 'Form sửa vẫn cho sửa Mã nhóm (phải khóa)'
        : 'Form thêm vẫn cho sửa Mã nhóm (mã tự sinh, phải khóa)',
    )
  }
  if (ten) {
    const required =
      ten.required ||
      ten.getAttribute('aria-required') === 'true' ||
      Boolean(ten.closest('.MuiFormControl-root')?.querySelector('.MuiFormLabel-asterisk, [aria-required="true"]'))
    if (!required) review.notes.push('Ô Tên nhóm chưa thấy dấu bắt buộc')
  }
  return review
}

export function buttonIncludes(root: HTMLElement, text: string): boolean {
  return findButton(root, text) != null
}

export function findButton(root: HTMLElement, text: string): HTMLButtonElement | null {
  const buttons = root.querySelectorAll('button')
  for (const node of Array.from(buttons)) {
    const label = controlLabel(node)
    if (label === text || label.includes(text)) return node as HTMLButtonElement
  }
  return null
}

/** Ô nhập nằm trong khối có nhãn đúng chữ (nhãn đặt phía trên ô). */
export function inputByLabel(root: HTMLElement, label: string): HTMLInputElement | null {
  const labels = root.querySelectorAll('label')
  for (const node of Array.from(labels)) {
    const text = (node.textContent ?? '').replace(/\s+/g, ' ').trim().replace(/\*$/, '').trim()
    if (text !== label) continue
    const shell = node.closest('.kiot-form-field') ?? node.parentElement?.parentElement
    const input = shell?.querySelector('input') as HTMLInputElement | null
    if (input) return input
  }
  return null
}

export function sidebarOf(screen: HTMLElement): HTMLElement | null {
  const forms = Array.from(screen.querySelectorAll('form'))
  return (
    forms.find((form) => (form.textContent ?? '').includes('Sắp xếp theo')) ??
    forms.find((form) => (form.textContent ?? '').includes('Đang hoạt động')) ??
    forms.find((form) => (form.textContent ?? '').includes('Tất cả nhóm')) ??
    null
  )
}
